'use client';

import type { LordiconName } from './lordicon-sets';

import { Player } from '@lordicon/react';
import { useRef, useState, useEffect } from 'react';

import { getIconByName } from './lordicon-sets';

export interface LordiconProps {
  icon: LordiconName;
  trigger?: 'hover' | 'click' | 'loop' | 'loop-on-hover' | 'morph' | 'boomerang';
  colors?: Record<string, string>;
  delay?: number;
  size?: number;
  className?: string;
  autoplay?: boolean;
}

export function Lordicon({
  icon,
  size = 32,
  colors = {},
  className,
  trigger = 'loop',
}: LordiconProps) {
  const playerRef = useRef<Player>(null);
  const [isHovered, setIsHovered] = useState(false);

  const iconDef = getIconByName(icon);

  // Play from beginning on mount only if autoplay and no special trigger
  useEffect(() => {
    if (trigger === 'loop' || trigger === 'morph' || trigger === 'boomerang') {
      playerRef.current?.play();
    } else if (trigger === 'loop-on-hover' && isHovered) {
      playerRef.current?.play();
    } else if (!trigger || trigger === 'hover' || trigger === 'click') {
      // do nothing until event happens
    }
  }, [trigger, isHovered]);

  // Event handlers depending on trigger
  function handleMouseEnter() {
    if (trigger === 'hover' || trigger === 'loop-on-hover') {
      playerRef.current?.play();
      setIsHovered(true);
    }
  }

  function handleMouseLeave() {
    if (trigger === 'hover' || trigger === 'loop-on-hover') {
      playerRef.current?.pause();
      setIsHovered(false);
    }
  }

  function handleClick() {
    if (trigger === 'click') {
      playerRef.current?.playFromBeginning();
    }
  }

  // onComplete logic for looping triggers
  function handleComplete() {
    if (trigger === 'loop' || trigger === 'loop-on-hover') {
      playerRef.current?.playFromBeginning();
    } else if (trigger === 'boomerang') {
      // if Player supports boomerang animation natively, no need to restart
      // else you might toggle play direction here (if API available)
    }
  }

  const colorString = Object.entries(colors)
    .map(([key, value]) => `${key}:${value}`)
    .join(',');

  return (
    <div
      style={{ width: size, height: size, display: 'inline-block' }}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {iconDef?.body && (
        <Player
          ref={playerRef}
          icon={iconDef.body}
          size={size}
          onComplete={handleComplete}
          colors={colorString}
        />
      )}
    </div>
  );
}
